import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowRight,
  Building2,
  Database,
  Download,
  FileClock,
  FolderKanban,
  Satellite,
  Users,
} from "lucide-react"

import { MetricStrip } from "@/components/metric-strip"
import { OrderStatusBadge } from "@/components/status-badge"
import { WorkspacePageHeader } from "@/components/workspace-page-header"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { adminOverviewQueryOptions } from "@/lib/query-options"

export const Route = createFileRoute("/admin/")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(adminOverviewQueryOptions()),
  head: () => ({
    meta: [{ title: "Operations overview · Service Portal demo" }],
  }),
  component: AdminOverviewPage,
})

function AdminOverviewPage() {
  const { data } = useSuspenseQuery(adminOverviewQueryOptions())
  const maxTrend = Math.max(...data.orderTrend.map((item) => item.value), 1)
  const maxSatellite = Math.max(
    ...data.imageryBySatellite.map((item) => item.count),
    1
  )

  return (
    <div className="mx-auto max-w-[1450px]">
      <WorkspacePageHeader
        kicker="Service Portal operations · synthetic environment"
        title="Operations overview"
        description="Monitor request volume, catalogue distribution, agencies, and release-ready packages across this resettable demonstration dataset."
        actions={
          <Button render={<Link to="/admin/orders" />}>
            <FolderKanban />
            Open order queue
          </Button>
        }
      />

      <MetricStrip
        className="mt-7"
        items={[
          {
            label: "Registered users",
            value: data.totals.users,
            detail: "Synthetic active identities",
            icon: Users,
            tone: "navy",
          },
          {
            label: "Catalogue scenes",
            value: data.totals.imagery.toLocaleString(),
            detail: "Representative inventory",
            icon: Database,
            tone: "cyan",
          },
          {
            label: "Open orders",
            value: data.totals.openOrders,
            detail: "Submitted through approval",
            icon: FileClock,
            tone: "yellow",
          },
          {
            label: "Ready packages",
            value: data.totals.readyDownloads,
            detail: "Within active release window",
            icon: Download,
            tone: "green",
          },
        ]}
      />

      <div className="mt-6 grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <section className="rounded-[14px] border border-border bg-white p-5 sm:p-6">
          <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
            <div>
              <h2 className="font-semibold text-[var(--navy)]">
                Request volume
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Monthly synthetic orders · February–July 2026
              </p>
            </div>
            <span className="text-xs font-semibold text-[var(--service-blue)]">
              +31% since February
            </span>
          </div>
          <div className="mt-8 flex h-[240px] items-end gap-3 sm:gap-5">
            {data.orderTrend.map((item, index) => (
              <div
                key={item.label}
                className="flex h-full flex-1 flex-col justify-end"
              >
                <div className="flex flex-1 items-end">
                  <div
                    className="relative w-full rounded-t-[6px] bg-[#b9dfe5] transition-[height,background-color] duration-300 hover:bg-[var(--cyan)]"
                    style={{
                      height: `${Math.max(12, (item.value / maxTrend) * 100)}%`,
                    }}
                  >
                    <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[0.65rem] font-semibold text-[var(--navy)]">
                      {item.value}
                    </span>
                  </div>
                </div>
                <p className="mt-2 text-center text-[0.65rem] font-medium text-muted-foreground">
                  {item.label}
                </p>
                {index === data.orderTrend.length - 1 ? (
                  <span className="sr-only">Current month</span>
                ) : null}
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-[14px] border border-border bg-white p-5 sm:p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold text-[var(--navy)]">
                Catalogue by mission
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Representative synthetic distribution
              </p>
            </div>
            <Satellite className="size-5 text-[var(--service-blue)]" />
          </div>
          <div className="mt-6 grid gap-4">
            {data.imageryBySatellite.map((item) => (
              <div key={item.satellite}>
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="font-semibold text-[var(--ink)]">
                    {item.satellite}
                  </span>
                  <span className="text-muted-foreground">
                    {item.count.toLocaleString()}
                  </span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#edf1f2]">
                  <div
                    className="h-full rounded-full bg-[var(--service-blue)]"
                    style={{ width: `${(item.count / maxSatellite) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <Button
            render={<Link to="/admin/imagery" />}
            variant="outline"
            className="mt-6 w-full"
          >
            Open imagery catalogue
            <ArrowRight />
          </Button>
        </section>
      </div>

      <section className="mt-6 overflow-hidden rounded-[14px] border border-border bg-white">
        <div className="flex flex-col justify-between gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center">
          <div>
            <h2 className="font-semibold text-[var(--navy)]">
              Orders needing attention
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              The same request state visible in the analyst workspace
            </p>
          </div>
          <Button
            render={<Link to="/admin/orders" />}
            variant="ghost"
            size="sm"
          >
            Manage queue
            <ArrowRight />
          </Button>
        </div>
        <div className="divide-y divide-border md:hidden">
          {data.recentOrders.slice(0, 5).map((order) => (
            <article key={order.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-[var(--navy)]">
                    {order.title}
                  </p>
                  <p className="mt-1 font-mono text-[0.62rem] text-muted-foreground">
                    {order.id}
                  </p>
                </div>
                <OrderStatusBadge status={order.status} />
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-3 rounded-[10px] bg-[var(--cloud)] p-3">
                <div>
                  <dt className="text-[0.62rem] text-muted-foreground">
                    Requester
                  </dt>
                  <dd className="mt-1 text-xs font-semibold text-[var(--ink)]">
                    {order.requester}
                  </dd>
                </div>
                <div>
                  <dt className="text-[0.62rem] text-muted-foreground">
                    Mission
                  </dt>
                  <dd className="mt-1 text-xs font-semibold text-[var(--ink)]">
                    {order.requestedSatellite}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-[0.62rem] text-muted-foreground">
                    Updated
                  </dt>
                  <dd className="mt-1 text-xs font-semibold text-[var(--ink)]">
                    {order.updatedLabel}
                  </dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
        <div className="hidden overflow-x-auto md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Requester</TableHead>
                <TableHead>Mission</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.recentOrders.slice(0, 5).map((order) => (
                <TableRow key={order.id}>
                  <TableCell className="max-w-[360px]">
                    <p className="truncate font-semibold text-[var(--navy)]">
                      {order.title}
                    </p>
                    <p className="mt-1 font-mono text-[0.62rem] text-muted-foreground">
                      {order.id}
                    </p>
                  </TableCell>
                  <TableCell className="text-xs">
                    <p className="font-medium text-[var(--ink)]">
                      {order.requester}
                    </p>
                    <p className="mt-1 text-muted-foreground">{order.agency}</p>
                  </TableCell>
                  <TableCell className="text-xs">
                    {order.requestedSatellite}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {order.updatedLabel}
                  </TableCell>
                  <TableCell>
                    <OrderStatusBadge status={order.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="mt-6 grid gap-4 sm:grid-cols-2">
        <Link
          to="/admin/agencies"
          className="group flex items-center justify-between gap-4 rounded-[14px] border border-border bg-white p-5 transition-colors hover:border-[#9eb6bf] focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[var(--cyan)]"
        >
          <span className="flex items-center gap-4">
            <span className="grid size-11 place-items-center rounded-[10px] bg-[#e8eef0] text-[var(--navy)]">
              <Building2 className="size-5" />
            </span>
            <span>
              <span className="block font-semibold text-[var(--navy)]">
                {data.totals.agencies} registered agencies
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">
                Membership, allocation, and approval state
              </span>
            </span>
          </span>
          <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
        </Link>
        <Link
          to="/admin/imagery"
          className="group flex items-center justify-between gap-4 rounded-[14px] border border-border bg-white p-5 transition-colors hover:border-[#9eb6bf] focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[var(--cyan)]"
        >
          <span className="flex items-center gap-4">
            <span className="grid size-11 place-items-center rounded-[10px] bg-[#e8f4f7] text-[var(--service-blue)]">
              <Database className="size-5" />
            </span>
            <span>
              <span className="block font-semibold text-[var(--navy)]">
                Catalogue operations
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">
                Scene metadata, mission, and release status
              </span>
            </span>
          </span>
          <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
        </Link>
      </section>
    </div>
  )
}
